// The words the records screens use for request kinds, states and outcomes
// (docs/plans/phase2-records-data-rights.md §6). Sentence case; the same word everywhere.
import type {
  AccessLogEntry,
  RecordReleaseView,
  RecordsRequestRow,
  RecordsRequestView,
} from '@wodi/shared';
import type { TagState } from '@wodi/ui';

type Kind = RecordsRequestRow['kind'];

export const KIND_WORDS: Record<Kind, string> = {
  access: 'Access',
  correction: 'Correction',
  objection: 'Objection',
  restriction: 'Restriction',
  deletion: 'Deletion',
};

/** What each kind asks for, in the requester's terms, for the log form. */
export const KIND_DESCRIPTIONS: Record<Kind, string> = {
  access: 'A copy of their record',
  correction: 'Something in the record is wrong',
  objection: 'They object to how their data is used',
  restriction: 'They want the use of their data limited',
  deletion: 'They want their record deleted',
};

export const RELATIONSHIP_WORDS: Record<RecordsRequestView['requesterRelationship'], string> = {
  self: 'The patient',
  parent_or_guardian: 'A parent or guardian',
  authorised_representative: 'An authorised representative',
};

export const CHANNEL_WORDS: Record<RecordsRequestView['channel'], string> = {
  in_person: 'In person',
  written: 'In writing',
  email: 'By email',
};

export const AREA_WORDS: Record<NonNullable<RecordsRequestView['correctionArea']>, string> = {
  demographics: 'Personal details (name, sex, date of birth, identifiers, phone)',
  clinical: 'The clinical record (notes, diagnoses, results)',
};

export const EVIDENCE_WORDS: Record<
  NonNullable<RecordsRequestView['identity']>['evidence'],
  string
> = {
  national_id: 'National ID seen',
  passport: 'Passport seen',
  birth_certificate: 'Birth certificate seen',
  alien_id: 'Alien ID seen',
  known_to_staff: 'Known to staff',
  other: 'Other',
};

export const OUTCOME_WORDS: Record<NonNullable<RecordsRequestRow['outcome']>, string> = {
  fulfilled: 'Fulfilled',
  partly_fulfilled: 'Partly fulfilled',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

export const FORMAT_WORDS: Record<RecordReleaseView['format'], string> = {
  printed_summary: 'Printed summary',
  machine_readable: 'File (JSON with FHIR)',
};

/** The request's state as a tag: overdue asks for action, closed is done. */
export function stateTag(
  row: Pick<RecordsRequestRow, 'state' | 'status' | 'outcome' | 'awaitingReview'>,
): {
  state: TagState;
  word: string;
} {
  if (row.status === 'closed')
    return { state: 'done', word: row.outcome === null ? 'Closed' : OUTCOME_WORDS[row.outcome] };
  if (row.state === 'overdue') return { state: 'attention', word: 'Overdue' };
  if (row.state === 'due_today') return { state: 'attention', word: 'Due today' };
  // A correction waiting for someone to accept or decline it (privacy tooling plan §5.2).
  if (row.awaitingReview) return { state: 'pending', word: 'Awaiting review' };
  return row.status === 'verified'
    ? { state: 'active', word: 'Identity checked' }
    : { state: 'pending', word: 'Identity not checked' };
}

export const ACCESS_KIND_WORDS: Record<AccessLogEntry['kind'], string> = {
  viewed: 'Viewed',
  changed: 'Changed',
  printed: 'Printed',
  exported: 'Exported',
  other: 'Other',
};

/** A readable name for an audited action: "record.exported" reads "Record exported". */
export function actionWords(action: string): string {
  const text = action.replace(/[._]/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
